import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import JsonLd from "@/components/JsonLd";
import FAQ from "@/components/FAQ";
import { blogPosts, blogCommon, BLOG_DATE } from "@/i18n/blogPosts";

const SITE = "https://reflectumpellicule.com";

export default function Blog() {
  const { lang, t } = useLanguage();
  const posts = blogPosts[lang];
  const c = blogCommon[lang];

  useEffect(() => {
    document.title = c.indexTitle;
    const tag = document.querySelector('meta[name="description"]');
    if (tag) tag.setAttribute("content", c.indexDesc);
    const canonical = document.querySelector('link[rel="canonical"]');
    const prev = canonical?.getAttribute("href");
    if (canonical) canonical.setAttribute("href", `${SITE}/conseils`);
    return () => {
      if (canonical && prev) canonical.setAttribute("href", prev);
    };
  }, [c, lang]);

  const blogLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: c.indexH1,
    url: `${SITE}/conseils`,
    inLanguage: lang,
    publisher: { "@type": "Organization", name: "Reflectum Pellicule", url: SITE },
    blogPost: posts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      description: p.metaDesc,
      datePublished: BLOG_DATE,
      inLanguage: lang,
      url: `${SITE}${p.path}`,
      author: { "@type": "Organization", name: "Reflectum Pellicule", url: SITE },
    })),
  };

  return (
    <div className="animate-fade-up">
      <JsonLd id="blog" data={blogLd} />
      <section className="py-20 sm:py-28 lg:py-40 bg-slate-50 border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600">{c.eyebrow}</div>
          <h1 data-testid="blog-title" className="font-heading text-[2.5rem] sm:text-5xl lg:text-6xl font-normal tracking-tight leading-[1.15] text-slate-900 mt-4">
            {c.indexH1}
          </h1>
          <p className="mt-8 text-lg text-slate-600 leading-relaxed">{c.indexIntro}</p>
        </div>
      </section>

      <section className="py-20 sm:py-28 lg:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 grid gap-8 md:grid-cols-3">
          {posts.map((p, i) => (
            <Link
              key={p.slug}
              to={p.path}
              data-testid={`blog-card-${i}`}
              className="group flex flex-col p-8 rounded-2xl bg-slate-50 border border-slate-200 hover:border-blue-300 transition-colors"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400">
                {c.published} {BLOG_DATE}
              </div>
              <h2 className="mt-4 font-heading text-2xl font-normal tracking-tight leading-snug text-slate-900 group-hover:text-blue-700 transition-colors">
                {p.title}
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed text-[15px] flex-1">{p.excerpt}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-blue-600 font-medium text-sm">
                {c.readMore}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Questions déplacées de l'accueil */}
      <FAQ
        items={t("faqMore.items")}
        jsonLdId="faq-blog"
        eyebrow={t("faqMore.eyebrow")}
        title={t("faqMore.title")}
        testId="faq-blog-section"
      />
    </div>
  );
}
